import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../queryKeys";
import { cdFetch, cdPost } from "../api";
import {
  BusinessCostUpdatePayload,
  BusinessCostSkuUpdatePayload,
  BusinessCostResetPayload,
  BusinessCostData,
  BusinessCostFiltersRes,
  WireBenchmarkItem,
} from "../../services/interfaces/IBusinessCostSetting";
import { useAppSelector } from "../../services/redux/hooks/hooks";

const getQueryParams = (
  commodity: string | null,
  email: string | null,
  baseSku?: string | null,
  negotiationWeek?: string | null,
) => {
  let qParams = "";
  if (commodity != null && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify([commodity])}&`;
  }
  if (email != null) {
    qParams += `email=${JSON.stringify(email)}&`;
  }
  if (commodity?.toLowerCase() === "copper wire" && baseSku) {
    qParams += `base_sku=${JSON.stringify(baseSku)}&`;
  }
  if (commodity?.toLowerCase() === "copper wire" && negotiationWeek) {
    qParams += `negotiation_week=${JSON.stringify(negotiationWeek)}&`;
  }
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }

  return qParams;
};

export const useBusinessCostFilter = (
  userEmail: string | null,
  commodity: string | null,
  baseSku?: string | null,
) => {
  const { data, isLoading } = useQuery({
    // queryKey: [queryKeys.businessCost, "filters", commodity],
    // queryFn: () => getFilters(commodity),
    queryKey: [
      queryKeys.businessCost,
      "filters",
      userEmail,
      commodity,
      baseSku,
    ],
    queryFn: () => getFilters(userEmail, commodity, baseSku),
    enabled:
      userEmail &&
        userEmail !== "" &&
        userEmail !== null &&
        userEmail !== undefined
        ? true
        : false,
  });
  let fetchFilters: BusinessCostFiltersRes = { ...data?.data };

  return { fetchFilters, isLoading };
};

const getFilters = (
  userEmail: string | null,
  commodity: string | null,
  baseSku?: string | null,
) => {
  const queryParams = getQueryParams(commodity, userEmail, baseSku);
  const res = cdFetch(`/getAllFiltersBusinessCostSetting${queryParams}`);
  // const res = cdFetch(
  //   `/getAllFiltersBusinessCostSetting?email=${JSON.stringify(userEmail)}`
  // );
  return res;
};

export const useBusinessCostData = (
  commodity: string | null,
  userEmail: string | null,
  baseSku: string | null,
  navItem: string | null,
  negotiationWeek?: string | null,
) => {
  const {
    data: res,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [queryKeys.businessCost, "data", commodity, userEmail, baseSku, negotiationWeek],
    queryFn: () => getBusinessCostData(commodity, userEmail, baseSku, negotiationWeek),
    enabled:
      !!commodity &&
        userEmail &&
        userEmail !== null &&
        userEmail !== undefined &&
        userEmail !== "" &&
        (navItem === "benchmarkBoardSetting" || navItem === "skuBoardAnalysis")
        ? true
        : false,
    staleTime: 0,
  });

  const data: Array<BusinessCostData> = res?.data?.data;
  const refreshDate: string | undefined = res?.data?.refresh_date ?? undefined;
  const yoy_date: string = res?.data?.yoy_date;
  return { data, refreshDate, yoy_date, isLoading, refetch };
};

const getBusinessCostData = (
  commodity: string | null,
  userEmail: string | null,
  baseSku?: string | null,
  negotiationWeek?: string | null,
) => {
  if (!commodity) return null;
  const queryParams = getQueryParams(commodity, userEmail, baseSku, negotiationWeek);

  const res = cdFetch(`/getBusinessCostSettingTableData${queryParams}`);
  return res;
};

export const useUpdateBusinessCost = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [queryKeys.businessCost, "cost update"],
    mutationFn: (payload: BusinessCostUpdatePayload) =>
      updateVolAllocationData(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.businessCost, "cost update"],
      });
      console.log("SUCCESSS", res);
    },
    onError: (error) => {
      console.log("ERROR", error);
    },
  });

  const skuMutation = useMutation({
    mutationKey: [queryKeys.businessCost, "sku cost update"],
    mutationFn: (payload: BusinessCostSkuUpdatePayload) =>
      updateSkuBoardCostData(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.businessCost, "sku cost update"],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.businessCost, "sku-analysis-data"],
      });
      console.log("SKU UPDATE SUCCESS", res);
    },
    onError: (error) => {
      console.log("SKU UPDATE ERROR", error);
    },
  });

  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    mutateSku: skuMutation.mutate,
    isSkuSuccess: skuMutation.isSuccess,
    isSkuError: skuMutation.isError,
    skuError: skuMutation.error,
    skuData: skuMutation.data,
  };
};

const updateVolAllocationData = (payload: BusinessCostUpdatePayload) => {
  const res = cdPost(`/updateBusinessCostSettingData`, payload);
  return res;
};

const updateSkuBoardCostData = (payload: BusinessCostSkuUpdatePayload) => {
  const res = cdPost(`/updateBoardCostSKUs`, payload);
  return res;
};

export const useResetBusinessCost = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [queryKeys.businessCost, "cost reset"],
    mutationFn: (payload: BusinessCostResetPayload) =>
      resetBusinessCostData(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.businessCost],
      });
      console.log("RESET SUCCESS", res);
    },
    onError: (error) => {
      console.log("RESET ERROR", error);
    },
  });

  return {
    resetMutate: mutation.mutate,
    isResetLoading: mutation.isPending,
    isResetSuccess: mutation.isSuccess,
    isResetError: mutation.isError,
    resetError: mutation.error,
    resetData: mutation.data,
  };
};

const resetBusinessCostData = (payload: BusinessCostResetPayload) => {
  const res = cdPost(`/resetBoardCostSetting`, payload);
  return res;
};

export function getBoardCostReportDataForExcel(
  userEmail: string | null,
  commodity: string | null,
  module: string,
  district: string[] | null,
) {
  let qParams = "";
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
  }
  if (district && district.length > 0) {
    qParams += `district=${JSON.stringify(district)}&`;
  }

  if (module && module.length > 0) {
    qParams += `module=${JSON.stringify(module)}&`;
  }
  if (userEmail && userEmail.length > 0) {
    qParams += `email=${JSON.stringify(userEmail)}&`;
  }

  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getBoardCostDownloadData${qParams}`);
  return res;
}

export function getBoardCostTrendReportDataForExcel(
  userEmail: string | null,
  commodity: string | null,
  period: string,
  district: string[] | null,
  baseSku: string | null,
) {
  let qParams = "";
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
  }

  if (period && period.length > 0) {
    qParams += `period=${JSON.stringify(period)}&`;
  }

  if (userEmail && userEmail.length > 0) {
    qParams += `email=${JSON.stringify(userEmail)}&`;
  }

  if (district && district.length > 0 && !district.includes("All")) {
    qParams += `district=${JSON.stringify(district)}&`;
  }

  if (baseSku && baseSku.length > 0) {
    qParams += `base_sku=${JSON.stringify(baseSku)}&`;
  }

  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getExcelForBusinessCostTrends${qParams}`);
  return res;
}

export function getRecommendedBCDataForExcel(
  userEmail: string | null,
  commodity: string | null,
  baseSku: string | null,
) {
  let qParams = "";
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
    if (commodity === "Copper Wire") {
      qParams += `base_sku=${JSON.stringify(baseSku)}&`;
    }
  }
  if (userEmail && userEmail.length) {
    qParams += `email=${JSON.stringify(userEmail)}&`;
  }
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }

  const res = cdFetch(`/downloadRecommendedBC${qParams}`);
  return res;
}

export const useSKUAnalysisData = (
  commodity: string | null,
  district: string | null,
  baseSKU: string | null,
  navItem: string | null,
) => {
  const {
    data: res,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      queryKeys.businessCost,
      "sku-analysis-data",
      commodity,
      district,
      baseSKU,
    ],
    queryFn: () => getSKUAnalysisData(commodity, district, baseSKU),
    enabled:
      !!commodity &&
        baseSKU &&
        baseSKU !== null &&
        baseSKU !== undefined &&
        baseSKU !== "" &&
        district &&
        district !== null &&
        district !== undefined &&
        district !== "" &&
        navItem === "skuBoardAnalysis"
        ? true
        : false,
    staleTime: 0,
  });

  const data: Array<BusinessCostData> = res?.data?.data?.data;
  const refreshDate: string | undefined = res?.data?.data?.refresh_date ?? undefined;

  return { data, isLoading, refetch, refreshDate };
};

export const getSKUAnalysisData = (
  commodity: string | null,
  district: string | null,
  baseSKU: string | null,
) => {
  if (!commodity) return null;
  let qParams = "";
  if (commodity != null && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
  }
  if (district != null) {
    qParams += `plant_group=${JSON.stringify(district)}&`;
  }
  if (baseSKU !== null) {
    qParams += `base_sku=${JSON.stringify(baseSKU)}&`;
  }
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getSKUAnalysisData${qParams}`);
  return res;
};

export const useOtherSKUsBC = (
  district: string | null,
  finalizedBoardCost: number | null,
  enabled: boolean,
  isRetained?: boolean,
) => {
  const {
    data: res,
    isLoading,
    refetch,
    isError,
    error,
  } = useQuery({
    queryKey: [
      queryKeys.businessCost,
      "other-skus-bc",
      district,
      finalizedBoardCost,
      isRetained,
    ],
    queryFn: () => getOtherSKUsBC(district, finalizedBoardCost, isRetained),
    enabled:
      enabled &&
      district !== null &&
      district !== "" &&
      (isRetained || (finalizedBoardCost !== null &&
      finalizedBoardCost !== undefined)),
    staleTime: 0,
  });

  const rawData: any[] = Array.isArray(res?.data?.data)
    ? res.data.data
    : Array.isArray(res?.data)
      ? res.data
      : [];

  const data: WireBenchmarkItem[] = isRetained
    ? rawData.map((item) => ({
        ...item,
        finalized_business_cost: item.actual_board_cost ?? item.finalized_business_cost,
      }))
    : rawData;

  return { data, isLoading, refetch, isError, error };
};

const getOtherSKUsBC = (
  district: string | null,
  finalizedBoardCost: number | null,
  isRetained?: boolean,
) => {
  if (!district) return null;
  if (!isRetained && (finalizedBoardCost === null || finalizedBoardCost === undefined)) {
    return null;
  }

  let qParams = `?district=${encodeURIComponent(district)}`;
  if (isRetained) {
    qParams += `&isRetained=1`;
  } else {
    const formattedFinalizedBoardCost = Number.isFinite(finalizedBoardCost)
      ? finalizedBoardCost!.toFixed(2)
      : String(finalizedBoardCost);
    qParams += `&finalized_board_cost=${encodeURIComponent(formattedFinalizedBoardCost)}`;
  }
  const res = cdFetch(`/getOtherSKUsBC${qParams}`);
  return res;
};
